import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { Page } from "../../../components/page-base";
import useUserStore from "../../../store/user";
import { fonts } from "../../../styles/theme";
import { NumeroAnimado } from "../../relatorio/componentes/numero-animado";
import { Barra, Card, Chip, Chips, Meta, Numero, cores } from "../../relatorio/styles";
import { adminOverviewService } from "../services";
import type { Fatia, VisaoGeral } from "../types";
import {
  CORES_DE_ACESSO,
  CORES_DE_STATUS,
  GOOGLE,
  ROTULO_DE_ACESSO,
  ROTULO_DE_STATUS,
  mesCurto,
  percentual,
  rotulo,
  topComOutros,
  total,
  ultimosDozeMeses,
} from "./graficos";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);
ChartJS.defaults.font.family = `${fonts.gilroyRegular}, system-ui, sans-serif`;
ChartJS.defaults.color = cores.ink3;

const Conteudo = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  min-width: 0;
  padding: 28px 32px 56px;
  background: #f8f9fa;
  font-family: ${fonts.gilroyRegular}, system-ui, sans-serif;
  color: ${cores.ink};
  box-sizing: border-box;

  h1 {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 26px;
    letter-spacing: -0.02em;
    margin: 8px 0 4px;
  }
  h2 {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 16px;
    letter-spacing: -0.01em;
    margin: 0;
  }
  @media (max-width: 899px) {
    padding: 20px 16px 48px;
  }
`;

const Kpis = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 22px 0;
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
`;

const Kpi = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
`;

const Rotulo = styled(Meta)`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  color: ${cores.ink3};
  font-size: 13px;
`;

const Painel = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
  @media (min-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Grafico = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  .area {
    position: relative;
    height: 300px;
  }
`;

const Legenda = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  li {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .cor {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex-shrink: 0;
  }
  .nome {
    flex: 1;
    color: ${cores.ink2};
  }
  .valor {
    font-family: ${fonts.gilroyBold}, sans-serif;
    font-variant-numeric: tabular-nums;
  }
`;

const TEMA = {
  azul: { fundo: GOOGLE.azulTinta, cor: GOOGLE.azul },
  verde: { fundo: GOOGLE.verdeTinta, cor: GOOGLE.verde },
  amarelo: { fundo: GOOGLE.amareloTinta, cor: GOOGLE.amarelo },
  vermelho: { fundo: GOOGLE.vermelhoTinta, cor: GOOGLE.vermelho },
};

const barrasHorizontais = (cor: string): ChartOptions<"bar"> => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { callbacks: { label: (contexto) => ` ${contexto.parsed.x} perfis` } },
  },
  scales: {
    x: { grid: { color: cores.line }, ticks: { precision: 0, font: { size: 11 } }, beginAtZero: true },
    y: { grid: { display: false }, ticks: { font: { size: 12 }, color: cores.ink2 } },
  },
  datasets: { bar: { backgroundColor: cor, borderRadius: 6, maxBarThickness: 22 } },
});

const rosca: ChartOptions<"doughnut"> = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "68%",
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (contexto) => ` ${contexto.parsed}` } } },
};

const linha: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (contexto) => ` ${contexto.parsed.y} perfis` } } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: { beginAtZero: true, grid: { color: cores.line }, ticks: { precision: 0, font: { size: 11 } } },
  },
  elements: { line: { tension: 0.35, borderWidth: 2.5, borderColor: GOOGLE.azul, fill: true, backgroundColor: "rgba(26,115,232,0.10)" }, point: { radius: 3, backgroundColor: GOOGLE.azul } },
};

const Rosca = ({ titulo, apoio, fatias, cores: paleta, rotulos }: { titulo: string; apoio: string; fatias: Fatia[]; cores: Record<string, string>; rotulos: Record<string, string> }) => {
  const soma = total(fatias);
  return (
    <Grafico>
      <div>
        <h2>{titulo}</h2>
        <Meta>{apoio}</Meta>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 200px) 1fr", gap: 20, alignItems: "center" }}>
        <div style={{ position: "relative", height: 200 }}>
          <Doughnut
            data={{
              labels: fatias.map((fatia) => rotulo(rotulos, fatia.label)),
              datasets: [{ data: fatias.map((fatia) => fatia.value), backgroundColor: fatias.map((fatia) => paleta[fatia.label] ?? GOOGLE.cinza), borderWidth: 2, borderColor: "#fff" }],
            }}
            options={rosca}
          />
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <Numero $tamanho={26}>{soma.toLocaleString("pt-BR")}</Numero>
            <Meta>total</Meta>
          </div>
        </div>
        <Legenda>
          {fatias.map((fatia) => (
            <li key={fatia.label}>
              <span className="cor" style={{ background: paleta[fatia.label] ?? GOOGLE.cinza }} />
              <span className="nome">{rotulo(rotulos, fatia.label)}</span>
              <span className="valor">{`${fatia.value.toLocaleString("pt-BR")} · ${percentual(fatia.value, soma)}%`}</span>
            </li>
          ))}
        </Legenda>
      </div>
    </Grafico>
  );
};

export const ProfileDashboard = () => {
  const { user } = useUserStore();
  const token = user?.token ?? "";
  const [uf, setUf] = useState<string | null>(null);
  const [dados, setDados] = useState<VisaoGeral | null>(null);
  const [ufsDisponiveis, setUfsDisponiveis] = useState<Fatia[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!token) return;
    let ativo = true;
    setCarregando(true);
    adminOverviewService.getVisaoGeral(token, uf).then((resposta) => {
      if (!ativo) return;
      setDados(resposta);
      if (resposta && !uf) setUfsDisponiveis(resposta.perfisPorUf);
      setCarregando(false);
    });
    return () => {
      ativo = false;
    };
  }, [token, uf]);

  const ufs = useMemo(() => ufsDisponiveis.filter((fatia) => fatia.label !== "Sem UF").slice(0, 10), [ufsDisponiveis]);
  const porUf = useMemo(() => topComOutros(dados?.perfisPorUf ?? [], 10), [dados]);
  const porCategoria = useMemo(() => topComOutros(dados?.perfisPorCategoria ?? [], 10), [dados]);
  const porMes = useMemo(() => ultimosDozeMeses(dados?.novosPerfisPorMes ?? []), [dados]);
  const cidades = useMemo(() => (dados?.perfisPorCidade ?? []).slice(0, 8), [dados]);
  const maiorCidade = cidades[0]?.value ?? 0;
  const geradoEm = dados?.generatedAt ? new Date(dados.generatedAt) : null;

  return (
    <Page.Root>
      <Conteudo id="dashboard-container">
        <Page.HeaderAux />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1>Panorama da base</h1>
            <Meta>
              Contagens reais do banco — perfis de clientes ativos, por região, categoria e situação de acesso.
              {geradoEm ? ` Atualizado ${geradoEm.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}.` : ""}
            </Meta>
          </div>
          <Chips role="tablist" aria-label="Filtrar por UF">
            <Chip type="button" role="tab" aria-selected={uf === null} $ativo={uf === null} onClick={() => setUf(null)}>
              Brasil
            </Chip>
            {ufs.map((fatia) => (
              <Chip key={fatia.label} type="button" role="tab" aria-selected={uf === fatia.label} $ativo={uf === fatia.label} onClick={() => setUf(fatia.label)}>
                {fatia.label}
              </Chip>
            ))}
          </Chips>
        </div>

        <Kpis>
          <Kpi $fundo={TEMA.azul.fundo}>
            <Rotulo>{uf ? `Perfis ativos em ${uf}` : "Perfis ativos"}</Rotulo>
            <Numero $tamanho={40} $cor={TEMA.azul.cor}>
              {dados ? <NumeroAnimado valor={dados.perfisAtivos} /> : "—"}
            </Numero>
            <Meta>{dados ? `de ${dados.clientesTotal.toLocaleString("pt-BR")} clientes cadastrados` : "carregando"}</Meta>
          </Kpi>
          <Kpi $fundo={TEMA.verde.fundo}>
            <Rotulo>Em acompanhamento</Rotulo>
            <Numero $tamanho={40} $cor={TEMA.verde.cor}>
              {dados ? <NumeroAnimado valor={dados.perfisEmAcompanhamento} /> : "—"}
            </Numero>
            <Meta>{dados ? `${percentual(dados.perfisEmAcompanhamento, dados.perfisAtivos)}% dos perfis ativos` : ""}</Meta>
          </Kpi>
          <Kpi $fundo={TEMA.amarelo.fundo}>
            <Rotulo>Contas gestoras conectadas</Rotulo>
            <Numero $tamanho={40} $cor={TEMA.amarelo.cor}>
              {dados ? <NumeroAnimado valor={dados.contasConectadas} /> : "—"}
            </Numero>
            <Meta>{dados ? `de ${dados.contasGestoras} contas · ${dados.convitesPendentes} convites pendentes` : ""}</Meta>
          </Kpi>
          <Kpi $fundo={TEMA.vermelho.fundo}>
            <Rotulo>Precisam reconectar</Rotulo>
            <Numero $tamanho={40} $cor={TEMA.vermelho.cor}>
              {dados ? <NumeroAnimado valor={dados.contasPrecisamReconectar} /> : "—"}
            </Numero>
            <Meta>contas com token vencido ou revogado</Meta>
          </Kpi>
        </Kpis>

        {carregando && !dados ? (
          <Meta>Carregando o panorama…</Meta>
        ) : dados ? (
          <Painel>
            <Grafico>
              <div>
                <h2>Perfis por estado</h2>
                <Meta>Perfis de clientes ativos, pelo endereço da ficha.</Meta>
              </div>
              <div className="area">
                <Bar data={{ labels: porUf.map((f) => f.label), datasets: [{ data: porUf.map((f) => f.value) }] }} options={barrasHorizontais(GOOGLE.azul)} />
              </div>
            </Grafico>

            <Grafico>
              <div>
                <h2>{uf ? `Categorias mais comuns em ${uf}` : "Categorias mais comuns"}</h2>
                <Meta>Categoria principal cadastrada no Google.</Meta>
              </div>
              <div className="area">
                <Bar data={{ labels: porCategoria.map((f) => f.label), datasets: [{ data: porCategoria.map((f) => f.value) }] }} options={barrasHorizontais(GOOGLE.verde)} />
              </div>
            </Grafico>

            <Rosca titulo="Clientes por status" apoio="Todos os clientes cadastrados, pelo status da conta." fatias={dados.clientesPorStatus} cores={CORES_DE_STATUS} rotulos={ROTULO_DE_STATUS} />

            <Rosca titulo={uf ? `Situação de acesso em ${uf}` : "Situação de acesso dos perfis ativos"} apoio="Última verificação com o Google: acesso, suspensão e cópia." fatias={dados.perfisPorAcesso} cores={CORES_DE_ACESSO} rotulos={ROTULO_DE_ACESSO} />

            <Grafico>
              <div>
                <h2>Novos perfis por mês</h2>
                <Meta>Perfis de clientes ativos cadastrados nos últimos 12 meses.</Meta>
              </div>
              <div className="area">
                <Line data={{ labels: porMes.map((f) => mesCurto(f.label)), datasets: [{ data: porMes.map((f) => f.value) }] }} options={linha} />
              </div>
            </Grafico>

            <Grafico>
              <div>
                <h2>{uf ? `Cidades com mais perfis em ${uf}` : "Cidades com mais perfis"}</h2>
                <Meta>As oito maiores concentrações da base.</Meta>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {cidades.map((cidade, indice) => (
                  <div key={cidade.label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14 }}>
                      <span style={{ color: cores.ink2 }}>{cidade.label}</span>
                      <span style={{ fontFamily: fonts.gilroyBold }}>{cidade.value.toLocaleString("pt-BR")}</span>
                    </div>
                    <Barra $largura={maiorCidade ? (cidade.value / maiorCidade) * 100 : 0} $atraso={0.2 + indice * 0.08} $cor={GOOGLE.azul} />
                  </div>
                ))}
                {cidades.length === 0 && <Meta>Sem cidades cadastradas para este filtro.</Meta>}
              </div>
            </Grafico>
          </Painel>
        ) : (
          <Meta>Não foi possível carregar o panorama.</Meta>
        )}

        <Meta style={{ display: "block", marginTop: 24 }}>
          “Perfil ativo” é o perfil de um cliente com status ativo no GeoRanking; o Google não informa se a ficha está ativa — o que ele informa (acesso, suspensão, cópia) está em “situação de acesso”.
        </Meta>
      </Conteudo>
    </Page.Root>
  );
};
